/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { Link } from "gatsby";
import { Cell, Row } from "../Grid/Grid";
import { Section } from "../../sharedComponents/Section/Section";
import { Img } from "../../sharedComponents/Images";
import { Icon } from "../Icons";
import { RCL } from "../RCL";
import * as style from "./Footer.module.scss";

const SocialLink = ({ href, target, title, icon }) => (
	<a href={href} target={target} title={title} className={icon}>
		<Icon name={icon} />
	</a>
);

const AppsLink = ({ href, target, title, img }) => (
	<a href={href} target={target} title={title}>
		<Img src={img} alt={title} />
	</a>
);

const Footer = ({
	className,
	columns,
	renderLinks,
	copyrightText,
	languageToggle,
	appStoreLinks,
	socialLinks,
}) => {
	useEffect(() => {
		// TODO:: add flag in contentful footer to avoid this check.
		// Hiding olark for simple footer
		let newStyle = null;
		if (!columns?.length) {
			newStyle = document.createElement("style");
			newStyle.innerHTML = `
				#hbl-live-chat-wrapper, #olark-wrapper{
					display: none !important;
				}
				`;
			document.head.appendChild(newStyle);
		}
		return () => {
			if (newStyle) document.head.removeChild(newStyle);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return (
		<Section as="footer" className={`${className} ${style.footer}`}>
			<React.Fragment key="footerColumns">
				{!!columns?.length && (
					<Row cols={`footer${columns?.length}`}>
						{columns?.map(column => {
							if (Array.isArray(column)) {
								return (
									<Cell key={column.id}>
										{column.map(columnItem => (
											<div key={columnItem.id} className={style.tab}>
												<input type="checkbox" id={columnItem.id} />
												<label
													className={cx(style.footerSectionTitle, style.tabLabel)}
													htmlFor={columnItem.id}>
													{columnItem.title}
													<span className={style.tabLabelChevron} />
												</label>
												<div className={style.tabContent}>
													<ul>
														{columnItem.items?.map(item => (
															<li key={item.id}>{renderLinks(item)}</li>
														))}
													</ul>
												</div>
											</div>
										))}
									</Cell>
								);
							}

							return (
								<Cell key={column.id}>
									<div className={cx(style.tab)}>
										<input type="checkbox" id={column.id} />
										<label
											className={cx(style.footerSectionTitle, style.tabLabel)}
											htmlFor={column.id}>
											{column.title}
											<span className={style.tabLabelChevron} />
										</label>
										<div className={style.tabContent}>
											<ul>
												{column.items?.map(item => (
													<li key={item.id}>{renderLinks(item)}</li>
												))}
											</ul>
										</div>
									</div>
								</Cell>
							);
						})}
					</Row>
				)}
				<div className={style.social}>
					{/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
					<Link to="#" className={style.toTop}>
						<RCL searchKey="back-to-top" />
					</Link>
					{!!socialLinks?.length && (
						<div className={style.socialNetworks}>
							{socialLinks?.map(
								link =>
									link.id && (
										<SocialLink
											key={link.id}
											href={link.link.url}
											target={link.link.target}
											title={link.title}
											icon={link.iconName}
										/>
									)
							)}
						</div>
					)}
					{!!appStoreLinks?.length && (
						<div className={style.appStores}>
							{appStoreLinks?.map(
								link =>
									link.id &&
									link.link.image[0]?.secure_url && (
										<AppsLink
											key={link.id}
											href={link.link.url}
											target={link.link.target}
											title={link.title}
											img={link.link.image[0].secure_url}
										/>
									)
							)}
						</div>
					)}
				</div>
				{copyrightText && (
					<div className={style.footerBottom}>
						{copyrightText && <div className={style.footerFineprint}>{copyrightText}</div>}
						{languageToggle && languageToggle}
					</div>
				)}
			</React.Fragment>
		</Section>
	);
};

Footer.propTypes = {
	className: PropTypes.string,
	columns: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			title: PropTypes.string,
			links: PropTypes.arrayOf(
				PropTypes.shape({
					id: PropTypes.string,
					title: PropTypes.string,
					url: PropTypes.string,
				})
			),
		})
	).isRequired,
	renderLinks: PropTypes.func,
	copyrightText: PropTypes.string.isRequired,
	languageToggle: PropTypes.node.isRequired,
	appStoreLinks: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			title: PropTypes.string,
			icon: PropTypes.object,
			link: PropTypes.shape({
				contentful_id: PropTypes.string,
				id: PropTypes.string,
				linkText: PropTypes.string,
				url: PropTypes.string,
				target: PropTypes.string,
				image: PropTypes.arrayOf(
					PropTypes.shape({
						secure_url: PropTypes.string,
						width: PropTypes.number,
						height: PropTypes.number,
						raw_transformation: PropTypes.string,
						version: PropTypes.number,
						public_id: PropTypes.string,
						format: PropTypes.string,
					})
				),
				imageAltText: PropTypes.string,
				icon: PropTypes.string,
				iconName: PropTypes.string,
				externalModalMessage: PropTypes.string,
			}),
			iconName: PropTypes.string,
		})
	).isRequired,
	socialLinks: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			link: PropTypes.shape({
				url: PropTypes.string,
				linkText: PropTypes.string,
				target: PropTypes.string,
			}),
			iconName: PropTypes.string,
		})
	).isRequired,
};

Footer.defaultProps = {
	className: undefined,
	renderLinks: link => <a href={link.url}>{link.title}</a>,
};

SocialLink.propTypes = {
	href: PropTypes.string.isRequired,
	target: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	icon: PropTypes.string.isRequired,
};

AppsLink.propTypes = {
	href: PropTypes.string.isRequired,
	target: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	img: PropTypes.string.isRequired,
};

export default Footer;
export { Footer };
